import { socket } from '../core/socket.js';
import { state } from '../core/state.js';
import { t } from '../core/i18n.js';
import { popToast } from '../utils/alerts.js';
import { formatSmartDate, convertirRaccourcisEnEmojis, escapeHtml } from '../utils/formatters.js';
import { construireLienInvitation } from './sidebar.js';
import {
    getUserEmojis, getSphereEmojis,
    onEmojisChange, offEmojisChange
} from '../core/customEmojis.js';
import { _insertTextAtCursor, _insertNodeAtCursor, _applyTwemojiInput, _majSaisie } from './chatInput.js';
import { applyTwemoji } from '../core/twemoji-setup.js';
import { triggerMessageEffect } from '../core/messageEffects.js';

// ==========================================================================
//   SCROLL
// ==========================================================================

export function scrollToBottom() {
    const messagesUl = document.getElementById('messages');
    if (!messagesUl) return;
    messagesUl.scrollTop = messagesUl.scrollHeight;
    setTimeout(() => {
        messagesUl.scrollTo({ top: messagesUl.scrollHeight, behavior: 'smooth' });
    }, 250);
}

export function scrollIfNearBottom() {
    const ul = document.getElementById('messages');
    if (!ul) return;
    const distanceFromBottom = ul.scrollHeight - ul.scrollTop - ul.clientHeight;
    if (distanceFromBottom < 200) scrollToBottom();
}

// Bouton retour en bas
(function () {
    const btn = document.getElementById('btn-scroll-bottom');
    const ul  = document.getElementById('messages');
    if (!btn || !ul) return;
    ul.addEventListener('scroll', () => {
        const distanceFromBottom = ul.scrollHeight - ul.scrollTop - ul.clientHeight;
        btn.classList.toggle('visible', distanceFromBottom > 200);
    });
})();

// ==========================================================================
//   EMOJI / GIF PICKER
// ==========================================================================

export function toggleEmojiPicker() {
    const container = document.getElementById('chat-emoji-picker-container');
    const reactionMenu = document.getElementById('full-emoji-picker-container');
    const gif = document.getElementById('gif-picker');
    const custom = document.getElementById('custom-emoji-picker');
    if (reactionMenu) reactionMenu.style.display = 'none';
    if (gif) gif.style.display = 'none';
    if (custom) custom.style.display = 'none';
    if (container.style.display === 'none' || container.style.display === '') {
        container.style.display = 'block';
    } else {
        container.style.display = 'none';
    }
}

export function toggleCustomEmojiPicker() {
    const container = document.getElementById('custom-emoji-picker');
    const emo = document.getElementById('chat-emoji-picker-container');
    const gif = document.getElementById('gif-picker');
    if (!container) return;
    if (emo) emo.style.display = 'none';
    if (gif) gif.style.display = 'none';
    if (container.style.display === 'none' || container.style.display === '') {
        container.style.display = 'block';
        window._customEmojiOpenedAt = Date.now();
        _renderCustomEmojiPicker();
    } else {
        container.style.display = 'none';
    }
}

(function () {
    const chatPicker = document.getElementById('chat-picker');
    const mainInput  = document.getElementById('input');
    if (!chatPicker || !mainInput) return;
    chatPicker.addEventListener('emoji-click', event => {
        const emoji = event.detail.unicode;
        mainInput.focus();
        _insertTextAtCursor(emoji);
        _applyTwemojiInput(mainInput);
        mainInput.dispatchEvent(new Event('input'));
        _majSaisie();
    });
})();

export function toggleGifPicker() {
    const gifContainer   = document.getElementById('gif-picker');
    const emojiContainer = document.getElementById('chat-emoji-picker-container');
    if (emojiContainer) emojiContainer.style.display = 'none';
    if (gifContainer.style.display === 'none' || gifContainer.style.display === '') {
        gifContainer.style.display = 'flex';
    } else {
        gifContainer.style.display = 'none';
    }
}

// Dernier onglet ouvert (GIF par défaut — choix utilisateur)
let _emojiGifLastTab = 'gif';

export function emojiGifTab(which) {
    _emojiGifLastTab = which;
    const emo    = document.getElementById('chat-emoji-picker-container');
    const gif    = document.getElementById('gif-picker');
    const custom = document.getElementById('custom-emoji-picker');
    if (emo)    emo.style.display    = which === 'emoji'  ? 'block' : 'none';
    if (gif)    gif.style.display    = which === 'gif'    ? 'flex'  : 'none';
    if (custom) custom.style.display = which === 'custom' ? 'block' : 'none';
    if (which === 'custom') _renderCustomEmojiPicker();
    const tabs = document.getElementById('emojigif-tabs');
    if (tabs) tabs.querySelectorAll('.egt-tab').forEach(b =>
        b.classList.toggle('active', b.dataset.tab === which));
}

// ── Picker d'emojis custom ────────────────────────────────────────────────────

function _fermerTousPickers() {
    ['chat-emoji-picker-container','gif-picker','custom-emoji-picker'].forEach(id => {
        const el = document.getElementById(id);
        if (el) el.style.display = 'none';
    });
    const tabs = document.getElementById('emojigif-tabs');
    if (tabs) tabs.style.display = 'none';
}

function _insererEmojiCustom(nom) {
    const input = document.getElementById('input');
    if (!input) return;
    const all = [
        ...getUserEmojis(),
        ...(state.sphereActuelleID ? getSphereEmojis(state.sphereActuelleID) : [])
    ];
    const emoji = all.find(e => e.nom === nom);
    if (!emoji) return;

    input.focus();
    const img = document.createElement('img');
    img.src = emoji.url;
    img.alt = `:${nom}:`;
    img.dataset.nom = nom;
    img.className = 'chat-emoji-custom';
    _insertNodeAtCursor(img);
    _insertTextAtCursor(' ');

    input.dispatchEvent(new Event('input'));
    if (typeof window._majSaisie === 'function') window._majSaisie();
    _fermerTousPickers();
}

// Formulaire d'upload inline (remplace prompt natif)
function _afficherFormulaireUpload(file, scope, sphereId) {
    const container = document.getElementById('custom-emoji-picker');
    if (!container) return;
    const nomSuggere = file.name.replace(/\.[^.]+$/, '').toLowerCase().replace(/[^a-z0-9_]/g, '_').slice(0, 32);
    const prevUrl = URL.createObjectURL(file);

    const form = document.createElement('div');
    form.className = 'cep-upload-form';
    form.innerHTML = `
        <img src="${prevUrl}" class="cep-upload-preview" alt="preview">
        <input class="cep-upload-nom cyber-input" type="text" maxlength="32"
               placeholder="${t('emoji.ask_name')}" value="${escapeHtml(nomSuggere)}" autocomplete="off" spellcheck="false">
        <div class="cep-upload-actions">
            <button class="cep-upload-cancel btn-alt">${t('emoji.btn_cancel')}</button>
            <button class="cep-upload-confirm">${t('emoji.btn_add')}</button>
        </div>
        <span class="cep-upload-hint">${t('emoji.upload_hint')}</span>
    `;
    container.prepend(form);

    const nomInput = form.querySelector('.cep-upload-nom');
    nomInput.focus();
    nomInput.select();

    const doUpload = async () => {
        const nom = nomInput.value.trim().toLowerCase().replace(/[^a-z0-9_]/g, '');
        if (!nom) { nomInput.focus(); return; }

        const route = scope === 'sphere' ? '/api/upload-sphere-emoji' : '/api/upload-user-emoji';
        const fd = new FormData();
        fd.append('emojiFile', file);

        form.querySelector('.cep-upload-confirm').disabled = true;
        form.querySelector('.cep-upload-confirm').textContent = '…';
        try {
            const res  = await fetch(route, { method: 'POST', body: fd });
            const data = await res.json();
            if (!data.success) { popToast(data.error || t('emoji.upload_error'), 'error'); }
            else if (scope === 'sphere') {
                socket.emit('add-sphere-emoji', { sphereId, nom, url: data.url });
            } else {
                socket.emit('add-user-emoji', { nom, url: data.url });
            }
        } catch { popToast(t('emoji.upload_error'), 'error'); }
        URL.revokeObjectURL(prevUrl);
        form.remove();
    };

    form.querySelector('.cep-upload-confirm').addEventListener('click', doUpload);
    form.querySelector('.cep-upload-cancel').addEventListener('click', () => {
        URL.revokeObjectURL(prevUrl);
        form.remove();
    });
    nomInput.addEventListener('keydown', e => {
        if (e.key === 'Enter') { e.preventDefault(); doUpload(); }
        if (e.key === 'Escape') { URL.revokeObjectURL(prevUrl); form.remove(); }
    });
}

function _renderCustomEmojiPicker() {
    const container = document.getElementById('custom-emoji-picker');
    if (!container) return;

    // Garde le formulaire d'upload en cours s'il est ouvert
    const formEnCours = container.querySelector('.cep-upload-form');

    const sphereId   = state.sphereActuelleID;
    const userList   = getUserEmojis();
    const sphereList = sphereId ? getSphereEmojis(sphereId) : [];
    const isAdmin    = state.monRoleActuel === 'admin';

    const buildGrid = (list, scope) => list.map(e => `
        <div class="cep-item" data-nom="${escapeHtml(e.nom)}">
            <img src="${escapeHtml(e.url)}" alt=":${escapeHtml(e.nom)}:" loading="lazy"
                 draggable="false">
            ${scope === 'sphere' ? `<button class="cep-save" data-id="${e.id}" title="${t('emoji.save_perso')}">⭐</button>` : ''}
            ${scope === 'user' || isAdmin ? `<button class="cep-del" data-id="${e.id}" data-scope="${scope}" title="${t('emoji.delete')}">×</button>` : ''}
        </div>`).join('');

    container.innerHTML = `
        <div class="cep-section">
            <div class="cep-section-header">
                <span>⭐ ${t('emoji.my_emojis')}</span>
                <label class="cep-upload-btn" title="${t('emoji.upload_perso')}">＋
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" class="cep-file-input" data-scope="user" style="display:none">
                </label>
            </div>
            <div class="cep-grid">
                ${userList.length ? buildGrid(userList, 'user') : `<span class="cep-empty">${t('emoji.empty_perso')}</span>`}
            </div>
        </div>
        ${sphereId ? `
        <div class="cep-section">
            <div class="cep-section-header">
                <span>🌐 ${escapeHtml(document.getElementById('current-sphere-name')?.textContent || `#${sphereId}`)}</span>
                ${isAdmin ? `<label class="cep-upload-btn" title="${t('emoji.upload_sphere')}">＋
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" class="cep-file-input" data-scope="sphere" style="display:none">
                </label>` : ''}
            </div>
            <div class="cep-grid">
                ${sphereList.length ? buildGrid(sphereList, 'sphere') : `<span class="cep-empty">${t('emoji.empty_sphere')}</span>`}
            </div>
        </div>` : ''}
    `;

    // Remet le formulaire si upload en cours
    if (formEnCours) container.prepend(formEnCours);

    window._insererEmojiCustom = _insererEmojiCustom;

    // mousedown + preventDefault pour ne pas perdre le focus du contenteditable
    container.querySelectorAll('.cep-item img').forEach(img => {
        img.addEventListener('mousedown', (e) => {
            e.preventDefault();
            const nom = img.closest('.cep-item').dataset.nom;
            _insererEmojiCustom(nom);
        });
    });

    // Long press → prévisualisation en grand
    container.querySelectorAll('.cep-item img').forEach(img => {
        let _lpTimer = null;
        const start = () => { _lpTimer = setTimeout(() => _previewEmoji(img.src, img.alt), 500); };
        const cancel = () => { clearTimeout(_lpTimer); };
        img.addEventListener('pointerdown', start);
        img.addEventListener('pointerup', cancel);
        img.addEventListener('pointerleave', cancel);
        img.addEventListener('contextmenu', e => e.preventDefault());
    });

    // Upload fichier → formulaire inline
    container.querySelectorAll('.cep-file-input').forEach(input => {
        input.addEventListener('change', e => {
            const file = e.target.files[0];
            if (!file) return;
            _afficherFormulaireUpload(file, input.dataset.scope, sphereId);
            input.value = '';
        });
    });

    // Supprimer
    container.querySelectorAll('.cep-del').forEach(btn => {
        btn.addEventListener('click', e => {
            e.stopPropagation();
            const id = parseInt(btn.dataset.id);
            if (btn.dataset.scope === 'sphere') socket.emit('delete-sphere-emoji', { sphereId, emojiId: id });
            else socket.emit('delete-user-emoji', { emojiId: id });
        });
    });

    // Sauvegarder emoji de sphère → perso
    container.querySelectorAll('.cep-save').forEach(btn => {
        btn.addEventListener('click', e => {
            e.stopPropagation();
            socket.emit('save-sphere-emoji', { emojiId: parseInt(btn.dataset.id) });
            popToast(t('emoji.saved_perso'), 'success');
        });
    });
}

function _previewEmoji(src, alt) {
    let overlay = document.getElementById('cep-preview-overlay');
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'cep-preview-overlay';
        overlay.addEventListener('click', () => overlay.remove());
        document.body.appendChild(overlay);
    }
    overlay.innerHTML = `<img src="${src}" alt="${alt}"><div class="cep-preview-nom">${alt}</div>`;
    overlay.style.display = 'flex';
}

// Re-render quand la liste change (nouvel emoji ajouté/supprimé en temps réel)
onEmojisChange(() => {
    const custom = document.getElementById('custom-emoji-picker');
    if (custom && custom.style.display !== 'none') _renderCustomEmojiPicker();
});

export function toggleEmojiGif() {
    const tabs = document.getElementById('emojigif-tabs');
    if (!tabs) return;
    const emo = document.getElementById('chat-emoji-picker-container');
    const gif = document.getElementById('gif-picker');
    if (tabs.style.display === 'flex') {
        tabs.style.display = 'none';
        if (emo) emo.style.display = 'none';
        if (gif) gif.style.display = 'none';
    } else {
        tabs.style.display = 'flex';
        window._emojiGifOpenedAt = Date.now();
        emojiGifTab(_emojiGifLastTab);
    }
}

// Clic en dehors → ferme le panneau (garde 250 ms pour éviter que le clic
// d'ouverture remonte et referme immédiatement le panneau).
document.addEventListener('click', (e) => {
    const tabs = document.getElementById('emojigif-tabs');
    if (!tabs || tabs.style.display !== 'flex') return;
    if ((Date.now() - (window._emojiGifOpenedAt || 0)) < 250) return;
    if (e.target.closest('#emojigif-tabs') || e.target.closest('#chat-emoji-picker-container')
        || e.target.closest('#gif-picker') || e.target.closest('#custom-emoji-picker')
        || e.target.closest('#btn-emojigif')) return;
    tabs.style.display = 'none';
    const emo = document.getElementById('chat-emoji-picker-container');
    const gif = document.getElementById('gif-picker');
    const custom = document.getElementById('custom-emoji-picker');
    if (emo) emo.style.display = 'none';
    if (gif) gif.style.display = 'none';
    if (custom) custom.style.display = 'none';
});

// Ferme le picker custom desktop au clic dehors
document.addEventListener('click', (e) => {
    const custom = document.getElementById('custom-emoji-picker');
    if (!custom || custom.style.display === 'none') return;
    if ((Date.now() - (window._customEmojiOpenedAt || 0)) < 250) return;
    if (e.target.closest('#custom-emoji-picker') || e.target.closest('#btn-custom-emoji')) return;
    custom.style.display = 'none';
});

// ==========================================================================
//   RÉPONSE / ÉDITION
// ==========================================================================

let reponseEnCours = null;
let editionEnCours = null;

export function getReponseEnCours() { return reponseEnCours; }
export function getEditionEnCours() { return editionEnCours; }

export function repondre(auteur, texteOriginal) {
    reponseEnCours = {
        auteur,
        texte: texteOriginal.length > 50 ? texteOriginal.substring(0, 50) + '...' : texteOriginal,
        // OFF par défaut : réponse purement textuelle ; opt-in via toggle.
        notifier: false
    };
    document.getElementById('reply-target-name').innerText = '@' + auteur;
    document.getElementById('reply-target-text').innerText = reponseEnCours.texte;
    _majBoutonReplyMention();
    document.getElementById('reply-preview-banner').style.display = 'flex';
    document.getElementById('input').focus();
}

export function annulerReponse() {
    reponseEnCours = null;
    document.getElementById('reply-preview-banner').style.display = 'none';
}

export function toggleReplyMention() {
    if (!reponseEnCours) return;
    reponseEnCours.notifier = !reponseEnCours.notifier;
    _majBoutonReplyMention();
}

function _majBoutonReplyMention() {
    const b = document.getElementById('reply-mention-toggle');
    if (!b) return;
    const on = !!(reponseEnCours && reponseEnCours.notifier);
    b.classList.toggle('actif', on);
    b.setAttribute('aria-pressed', on ? 'true' : 'false');
    b.textContent = on ? '🔔 Mention on' : '🔕 Mention off';
    b.title = on
        ? 'La personne sera notifiée (comme une mention @). Clique pour repasser en réponse textuelle.'
        : 'Réponse textuelle, sans notification. Clique pour notifier la personne.';
}

export function commencerEdition(msg) {
    annulerEdition(); // ferme toute édition précédente
    editionEnCours = { id: msg.id, texteOriginal: msg.texte };
    annulerReponse();

    const li = document.querySelector(`.message-item[data-msg-id="${CSS.escape(String(msg.id))}"]`);
    if (!li) return;

    const messageContent = li.querySelector('.message-content');
    if (!messageContent) return;

    messageContent.style.display = 'none';
    li.classList.add('en-edition');

    const editor = document.createElement('div');
    editor.className = 'inline-edit-container';
    editor.innerHTML = `
        <textarea class="inline-edit-textarea" maxlength="2000"></textarea>
        <div class="inline-edit-hint">
            <span>Échap pour <button class="inline-edit-btn inline-edit-cancel">annuler</button></span>
            <span>Entrée pour <button class="inline-edit-btn inline-edit-confirm">sauvegarder</button></span>
        </div>`;
    messageContent.insertAdjacentElement('afterend', editor);

    const textarea = editor.querySelector('.inline-edit-textarea');
    textarea.value = msg.texte;
    textarea.style.height = 'auto';
    textarea.style.height = textarea.scrollHeight + 'px';
    textarea.focus();
    textarea.setSelectionRange(textarea.value.length, textarea.value.length);

    textarea.addEventListener('input', () => {
        textarea.style.height = 'auto';
        textarea.style.height = textarea.scrollHeight + 'px';
    });
    textarea.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { e.preventDefault(); annulerEdition(); }
        else if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); _confirmerEdition(); }
    });
    editor.querySelector('.inline-edit-cancel').addEventListener('click', () => annulerEdition());
    editor.querySelector('.inline-edit-confirm').addEventListener('click', () => _confirmerEdition());
}

function _confirmerEdition() {
    if (!editionEnCours) return;
    const li = document.querySelector(`.message-item[data-msg-id="${CSS.escape(String(editionEnCours.id))}"]`);
    const textarea = li?.querySelector('.inline-edit-textarea');
    const texte = textarea?.value?.trim() ?? '';
    if (texte && texte !== editionEnCours.texteOriginal) {
        socket.emit('modifier-message', { id: editionEnCours.id, nouveauTexte: texte });
    }
    annulerEdition();
}

export function annulerEdition() {
    if (!editionEnCours) return;
    const li = document.querySelector(`.message-item[data-msg-id="${CSS.escape(String(editionEnCours.id))}"]`);
    if (li) {
        li.classList.remove('en-edition');
        const messageContent = li.querySelector('.message-content');
        if (messageContent) messageContent.style.display = '';
        li.querySelector('.inline-edit-container')?.remove();
    }
    editionEnCours = null;
}

// ==========================================================================
//   RÉACTIONS
// ==========================================================================

let messageIdEnAttenteDeReaction = null;

// Appelé depuis app.js (actionMessage) pour pointer sur le message cible
// avant d'ouvrir le picker emoji via ouvrirFullEmoji.
export function setReactionMessageId(id) {
    messageIdEnAttenteDeReaction = id;
}

let _quickMenuTwemojiDone = false;

export function ouvrirMenuReaction(event, messageId) {
    messageIdEnAttenteDeReaction = messageId;
    const menu = document.getElementById('quick-reaction-menu');
    // Applique Twemoji une seule fois, au premier affichage (menu visible → parse OK)
    if (!_quickMenuTwemojiDone && window.twemoji) {
        window.twemoji.parse(menu, {
            folder: 'svg', ext: '.svg',
            base: 'https://cdn.jsdelivr.net/gh/jdecked/twemoji@15/assets/',
            className: 'twemoji',
        });
        _quickMenuTwemojiDone = true;
    }
    menu.style.display = 'flex';
    const rect = event.target.getBoundingClientRect();
    menu.style.left = `${rect.left - 50}px`;
    menu.style.top  = `${rect.top - 45}px`;
}

export function envoyerReaction(emoji) {
    if (!messageIdEnAttenteDeReaction) return;
    socket.emit('ajouter-reaction', { messageId: messageIdEnAttenteDeReaction, emoji });
    document.getElementById('quick-reaction-menu').style.display = 'none';
    messageIdEnAttenteDeReaction = null;
}

export function ouvrirFullEmoji() {
    const quickMenu = document.getElementById('quick-reaction-menu');
    const fullMenu  = document.getElementById('full-emoji-picker-container');
    const rect = quickMenu.getBoundingClientRect();
    fullMenu.style.top  = rect.top > window.innerHeight - 450
        ? `${rect.top - 410}px`
        : `${rect.bottom + 10}px`;
    fullMenu.style.left = `${Math.max(10, rect.left - 200)}px`;
    quickMenu.style.display = 'none';
    fullMenu.style.display  = 'block';
    // Le picker réactions était caché → son shadow DOM vient d'être rendu
    if (typeof window.initTwemojiPickers === 'function') window.initTwemojiPickers();
}

export function ouvrirFullEmojiDepuisBtn(e, messageId) {
    setReactionMessageId(messageId);
    const fullMenu = document.getElementById('full-emoji-picker-container');
    const rect = e.currentTarget.getBoundingClientRect();
    fullMenu.style.top  = rect.top > window.innerHeight - 450
        ? `${rect.top - 420}px`
        : `${rect.bottom + 6}px`;
    fullMenu.style.left = `${Math.max(10, rect.left - 180)}px`;
    fullMenu.style.display = 'block';
}

(function () {
    const reactionPicker = document.querySelector('#full-emoji-picker-container emoji-picker');
    if (!reactionPicker) return;
    reactionPicker.addEventListener('emoji-click', event => {
        envoyerReaction(event.detail.unicode);
        document.getElementById('full-emoji-picker-container').style.display = 'none';
    });
})();

export function construireReactionsMessage(messageId, reactionsObj, pseudoClient) {
    if (!messageId) return;
    const container = document.getElementById(`reactions-for-${messageId}`);
    if (!container) return;
    container.innerHTML = '';
    if (!reactionsObj || Object.keys(reactionsObj).length === 0) return;
    for (const [emoji, users] of Object.entries(reactionsObj)) {
        const badge = document.createElement('div');
        badge.className = 'reaction-badge';
        if (users.includes(pseudoClient)) badge.classList.add('je-suis-dessus');
        badge.title = users.slice(0, 10).join(', ') + (users.length > 10 ? '...' : '');
        badge.innerHTML = `<span class="reaction-emoji">${emoji}</span> <span class="reaction-count">${users.length}</span>`;
        applyTwemoji(badge);
        badge.onclick = (e) => {
            e.stopPropagation();
            socket.emit('ajouter-reaction', { messageId, emoji });
        };
        container.appendChild(badge);
    }
}

socket.on('maj-reaction', (data) => {
    construireReactionsMessage(data.messageId, data.reactions, state.monPseudo);
    scrollIfNearBottom();
});

// ==========================================================================
//   MENU AJOUT (bouton "+")
// ==========================================================================

export function toggleMenuAjout(e) {
    if (e) e.stopPropagation();
    const m = document.getElementById('attach-menu');
    if (!m) return;
    m.style.display = (m.style.display === 'none' || !m.style.display) ? 'flex' : 'none';
}

export function fermerMenuAjout() {
    const m = document.getElementById('attach-menu');
    if (m) m.style.display = 'none';
}

document.addEventListener('click', (e) => {
    if (!(e.target.closest && e.target.closest('.attach-wrap'))) fermerMenuAjout();
});

// ==========================================================================
//   LIGHTBOX
// ==========================================================================

document.getElementById('messages')?.addEventListener('click', (e) => {
    // Clic sur un Twemoji dans un message → rejouer l'effet si c'est un emoji déclencheur
    if (e.target.classList.contains('twemoji') && e.target.closest('.message-content')) {
        const emoji = e.target.alt || '';
        if (emoji) triggerMessageEffect(emoji);
        return;
    }
    // Lightbox pour les vraies images (pas les emojis)
    if (e.target.tagName === 'IMG'
        && e.target.closest('.message-content')
        && !e.target.classList.contains('chat-emoji-custom')
        && !e.target.classList.contains('twemoji')
        && !e.target.classList.contains('emoji')) {
        document.getElementById('lightbox-image').src = e.target.src;
        document.getElementById('lightbox-overlay').style.display = 'flex';
    }
});

export function fermerLightbox() {
    document.getElementById('lightbox-overlay').style.display = 'none';
    document.getElementById('lightbox-image').src = '';
}

// ==========================================================================
//   MESSAGES ÉPINGLÉS
// ==========================================================================

let _epinglesRenderToken = 0;
socket.on('reception-epingles', async (messages) => {
    const container = document.getElementById('pins-list-container');

    // Anti-race : ce handler est async (attente SBK + déchiffrement). Plusieurs
    // reception-epingles peuvent arriver en parallèle (ex. après plusieurs
    // demander-epingles). Si chacun vidait puis ajoutait après son await, les
    // cartes s'accumuleraient (doublons). On numérote chaque rendu et on ne
    // committe le DOM (vidage + ajout, atomique) qu'à la fin, si on est toujours
    // le rendu le plus récent.
    const renderToken = ++_epinglesRenderToken;

    if (messages.length === 0) {
        if (renderToken === _epinglesRenderToken) {
            container.innerHTML = `<p style='color:#8b949e;text-align:center;margin-top:20px;'>${t('pins.empty')}</p>`;
        }
        return;
    }

    // Pré-charger le coffre SBK une seule fois pour tout le lot (évite N appels
    // réseau si plusieurs messages épinglés sont E2EE et non en cache local).
    let sbkBackups = new Map();
    const destinataire = state.destinataireID;
    if (destinataire && window.ChaosSessionBackup && window.ChaosKeystore) {
        try { sbkBackups = await ChaosSessionBackup.restaurerEnvois(destinataire, socket); } catch (_) {}
    }

    // On construit les cartes dans un fragment détaché ; commit DOM à la fin.
    const fragment = document.createDocumentFragment();
    const vus = new Set(); // dédup par logical_id (ceinture + bretelles)

    for (const msg of messages) {
        // Sécurité : ne jamais afficher deux fois le même message logique.
        const dedupKey = msg.message_logical_id || String(msg.id);
        if (vus.has(dedupKey)) continue;
        vus.add(dedupKey);

        // Résoudre le texte : E2EE en MP → déchiffrer côté client.
        if (msg.algo_version > 0 && msg.contenu_chiffre && window.ChaosMPCrypto) {
            const logicalId = msg.message_logical_id;
            let texte = null;

            // 1) Cache local IndexedDB (message déjà vu sur ce device).
            if (logicalId && window.ChaosKeystore) {
                try {
                    const stored = await ChaosKeystore.getSentByLogicalId(logicalId)
                        || await ChaosKeystore.getMessageByLogicalId?.(logicalId);
                    if (stored?.texte) texte = stored.texte;
                } catch (_) {}
            }

            // 2) Coffre SBK (vu sur n'importe quel device de l'expéditeur ou du destinataire).
            if (!texte && logicalId && sbkBackups.has(logicalId)) {
                texte = sbkBackups.get(logicalId);
            }

            // 3) Déchiffrement Olm direct (session encore valide sur ce device).
            if (!texte && msg.device_expediteur) {
                try {
                    texte = await ChaosMPCrypto.dechiffrerMessage(
                        msg.contenu_chiffre, msg.auteur, msg.device_expediteur, null
                    );
                    // Mettre en cache local pour les prochaines ouvertures.
                    if (texte && logicalId && window.ChaosKeystore) {
                        ChaosKeystore.saveMessage({
                            id: String(msg.id), auteur: msg.auteur,
                            interlocuteur: msg.auteur, texte,
                            date_envoi: new Date(msg.date_envoi).getTime(),
                            message_logical_id: logicalId
                        }).catch(() => {});
                    }
                } catch (_) {}
            }

            // Fallback : placeholder non alarmant (le rekey n'est PAS déclenché
            // depuis le panel épingles pour ne pas générer de tempête additionnelle ;
            // la prochaine ouverture de la conversation le récupèrera naturellement).
            msg.texte = texte || '*(message chiffré — ouvre la conversation pour le resynchroniser)*';
        }

        const dateHtml = formatSmartDate(msg.date_envoi);
        let contenuPropre = msg.texte;
        if (!contenuPropre.includes('tenor.com') && !contenuPropre.includes('giphy.com')) {
            contenuPropre = DOMPurify.sanitize(marked.parse(contenuPropre));
        } else {
            contenuPropre = `<img src="${contenuPropre}" style="max-width:100%;border-radius:4px;">`;
        }

        // Construire la carte en DOM (pas innerHTML +=) pour pouvoir attacher
        // des event listeners directs sur les boutons Rejoindre et Désépingler.
        const card = document.createElement('div');
        card.className = 'pinned-msg-card';
        card.innerHTML = `
            <div class="pinned-msg-header">
                <strong style="color:${msg.couleur_bdd || '#58a6ff'}">@${escapeHtml(msg.auteur)}</strong>
                <span>${dateHtml}</span>
            </div>
            <div class="pinned-msg-body" style="font-size:0.9em;word-wrap:break-word;color:#c9d1d9;">${contenuPropre}</div>
            <div class="pinned-action-bar"></div>`;

        // Bouton Rejoindre : scroll jusqu'au message dans la conversation
        const btnJump = document.createElement('button');
        btnJump.className = 'btn-jump';
        btnJump.textContent = t('pins.jump') || 'Rejoindre';
        btnJump.addEventListener('click', () => {
            const logicalId = msg.message_logical_id;
            const msgId = String(msg.id);
            let el = document.querySelector(`.message-item[data-msg-id="${CSS.escape(msgId)}"]`);
            if (!el && logicalId) {
                el = document.querySelector(`.message-item[data-logical-id="${CSS.escape(logicalId)}"]`);
            }
            if (el) {
                // Fermer le panel et scroller jusqu'au message avec un flash visuel
                document.getElementById('pins-side-panel')?.classList.remove('open');
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.classList.add('msg-highlight');
                setTimeout(() => el.classList.remove('msg-highlight'), 2000);
            } else {
                popToast(t('pins.not_visible') || 'Message non visible — ouvre la conversation pour le voir');
            }
        });

        // Bouton Désépingler : toggle direct depuis le panel
        const btnUnpin = document.createElement('button');
        btnUnpin.className = 'btn-unpin';
        btnUnpin.textContent = t('pins.unpin') || '📌 Désépingler';
        btnUnpin.addEventListener('click', () => {
            socket.emit('toggle-epingle', {
                messageId: msg.id,
                salonId: state.salonActuelID,
                destinataire: state.destinataireID
            });
        });

        card.querySelector('.pinned-action-bar').append(btnJump, btnUnpin);
        fragment.appendChild(card);
    }

    // Commit atomique : seul le rendu le plus récent gagne (sinon on abandonne
    // pour ne pas écraser un rendu plus frais déclenché entre-temps).
    if (renderToken !== _epinglesRenderToken) return;
    container.innerHTML = '';
    container.appendChild(fragment);
});

// ==========================================================================
//   BULLES VOCALES / VIDÉO / INVITATION SPHÈRE
// ==========================================================================

export async function renderVoiceBubble(container, descripteurStr) {
    let d;
    try { d = JSON.parse(descripteurStr); } catch (_) { container.textContent = '🎙️'; return; }

    const bubble = document.createElement('div');
    bubble.className = 'voice-bubble';
    const dureeTxt = d.dur
        ? ` <span class="voice-dur">${Math.floor(d.dur / 60)}:${String(d.dur % 60).padStart(2, '0')}</span>`
        : '';

    if (!d.e2ee) {
        bubble.innerHTML = `<i class="fas fa-microphone voice-icon"></i><audio controls preload="metadata" src="${d.url}"></audio>${dureeTxt}`;
        container.appendChild(bubble);
        return;
    }

    // E2EE : déchiffrement en mémoire, lecture via blob URL.
    bubble.classList.add('voice-loading');
    bubble.innerHTML = `<i class="fas fa-lock voice-icon"></i> ${t('voicemsg.label')}…`;
    container.appendChild(bubble);
    try {
        const b64ToU8 = b64 => { const s = atob(b64); const u = new Uint8Array(s.length); for (let i = 0; i < s.length; i++) u[i] = s.charCodeAt(i); return u; };
        const resp   = await fetch(d.url);
        const ciffre = await resp.arrayBuffer();
        const key    = await crypto.subtle.importKey('raw', b64ToU8(d.key), { name: 'AES-GCM' }, false, ['decrypt']);
        const clair  = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: b64ToU8(d.iv) }, key, ciffre);
        const url    = URL.createObjectURL(new Blob([clair], { type: d.mime || 'audio/webm' }));
        bubble.classList.remove('voice-loading');
        bubble.innerHTML = `<i class="fas fa-microphone voice-icon"></i><audio controls preload="metadata" src="${url}"></audio>${dureeTxt}`;
    } catch (err) {
        console.error('[Vocal] Déchiffrement échoué :', err);
        bubble.classList.remove('voice-loading');
        bubble.classList.add('voice-error');
        bubble.innerHTML = `<i class="fas fa-lock voice-icon"></i> ${t('voicemsg.decrypt_error')}`;
    }
}

// Déchiffre un blob E2EE (descripteur {url, key, iv, mime}) et renvoie une
// blob URL en mémoire. Lève en cas d'échec. b64 → Uint8Array inline.
async function _dechiffrerBlobE2EE(d) {
    const b64ToU8 = b64 => { const s = atob(b64); const u = new Uint8Array(s.length); for (let i = 0; i < s.length; i++) u[i] = s.charCodeAt(i); return u; };
    const resp   = await fetch(d.url);
    const ciffre = await resp.arrayBuffer();
    const key    = await crypto.subtle.importKey('raw', b64ToU8(d.key), { name: 'AES-GCM' }, false, ['decrypt']);
    const clair  = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: b64ToU8(d.iv) }, key, ciffre);
    return URL.createObjectURL(new Blob([clair], { type: d.mime || 'application/octet-stream' }));
}

export async function renderVideoBubble(container, descripteurStr) {
    let d;
    try { d = JSON.parse(descripteurStr); } catch (_) { container.textContent = '🎬'; return; }

    const wrap  = document.createElement('div');
    wrap.className = 'video-bubble';
    const video = document.createElement('video');
    video.className  = 'message-video';
    video.controls   = true;
    video.preload    = 'metadata';
    video.playsInline = true;
    wrap.appendChild(video);
    if (d.caption) {
        const cap = document.createElement('div');
        cap.className = 'video-caption';
        cap.textContent = d.caption;
        wrap.appendChild(cap);
    }
    container.appendChild(wrap);

    if (!d.e2ee) { video.src = d.url; return; }
    try { video.src = await _dechiffrerBlobE2EE(d); }
    catch (err) {
        console.error('[Vidéo E2EE] Déchiffrement échoué :', err);
        wrap.innerHTML = `<i class="fas fa-lock"></i> ${t('voicemsg.decrypt_error')}`;
    }
}

export async function renderImageBubble(container, descripteurStr) {
    let d;
    try { d = JSON.parse(descripteurStr); } catch (_) { container.textContent = '🖼️'; return; }

    const wrap = document.createElement('div');
    wrap.className = 'image-bubble';
    const img = document.createElement('img');
    img.className = 'message-image';
    img.alt = d.name || '';
    wrap.appendChild(img);
    if (d.caption) {
        const cap = document.createElement('div');
        cap.className = 'video-caption';
        cap.textContent = d.caption;
        wrap.appendChild(cap);
    }
    container.appendChild(wrap);

    if (!d.e2ee) { img.src = d.url; return; }
    try {
        img.src = await _dechiffrerBlobE2EE(d);
    } catch (err) {
        console.error('[Image E2EE] Déchiffrement échoué :', err);
        wrap.innerHTML = `<i class="fas fa-lock"></i> ${t('voicemsg.decrypt_error')}`;
    }
}

export async function renderFileBubble(container, descripteurStr) {
    let d;
    try { d = JSON.parse(descripteurStr); } catch (_) { container.textContent = '📁'; return; }

    const wrap = document.createElement('div');
    wrap.className = 'file-bubble';
    const nom = d.name || 'fichier';
    wrap.innerHTML = `<i class="fas fa-lock file-bubble-icon"></i> <span class="file-bubble-name">${escapeHtml(nom)}</span>`;
    container.appendChild(wrap);
    if (d.caption) {
        const cap = document.createElement('div');
        cap.className = 'video-caption';
        cap.textContent = d.caption;
        container.appendChild(cap);
    }

    const lien = document.createElement('a');
    lien.className = 'file-bubble-dl';
    lien.textContent = t('filemsg.download') || '⬇️ Télécharger';
    lien.style.cursor = 'pointer';
    wrap.appendChild(lien);

    if (!d.e2ee) { lien.href = d.url; lien.download = nom; return; }
    lien.addEventListener('click', async (e) => {
        e.preventDefault();
        lien.textContent = '…';
        try {
            const url = await _dechiffrerBlobE2EE(d);
            const a = document.createElement('a');
            a.href = url; a.download = nom;
            document.body.appendChild(a); a.click(); a.remove();
            lien.textContent = t('filemsg.download') || '⬇️ Télécharger';
        } catch (err) {
            console.error('[Fichier E2EE] Déchiffrement échoué :', err);
            lien.textContent = t('voicemsg.decrypt_error');
        }
    });
}

export function renderSphereInvite(container, descripteurStr) {
    let d;
    try { d = JSON.parse(descripteurStr); } catch (_) { container.textContent = '🎫'; return; }
    const code = String(d.code || '');
    const lien = code ? construireLienInvitation(code) : '';

    const card = document.createElement('div');
    card.className = 'sphere-invite-card';

    const head = document.createElement('div');
    head.className = 'sic-head';
    if (d.sphereIcone) {
        const raw = d.sphereIcone;
        const iconeUrl = (raw.startsWith('/') || raw.startsWith('http'))
            ? raw
            : (/\.(jpg|jpeg|png|gif|webp)$/i.test(raw) ? `/img/spheres/${raw}` : null);
        if (iconeUrl) {
            const img = document.createElement('img');
            img.className = 'sic-icone'; img.src = iconeUrl; img.alt = '';
            head.appendChild(img);
        } else {
            const ph = document.createElement('div');
            ph.className = 'sic-icone sic-icone-ph';
            ph.textContent = raw.length <= 2 ? raw : (d.sphereNom || '?').charAt(0).toUpperCase();
            head.appendChild(ph);
        }
    } else {
        const ph = document.createElement('div');
        ph.className = 'sic-icone sic-icone-ph';
        ph.textContent = (d.sphereNom || '?').charAt(0).toUpperCase();
        head.appendChild(ph);
    }
    const titre = document.createElement('div');
    titre.className = 'sic-titre-wrap';
    const lbl = document.createElement('div');
    lbl.className = 'sic-label'; lbl.textContent = '🎫 Invitation';
    const nom = document.createElement('div');
    nom.className = 'sic-nom'; nom.textContent = d.sphereNom || 'une sphère';
    titre.appendChild(lbl); titre.appendChild(nom);
    head.appendChild(titre);
    card.appendChild(head);

    if (d.sphereDesc) {
        const desc = document.createElement('div');
        desc.className = 'sic-desc'; desc.textContent = d.sphereDesc;
        card.appendChild(desc);
    }

    const btn = document.createElement('button');
    btn.type = 'button'; btn.className = 'sic-join';
    btn.textContent = '🎫 Rejoindre la sphère';
    btn.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!code) return;
        socket.emit('rejoindre-via-code', code);
        btn.disabled = true;
        btn.textContent = 'Accès en cours…';
    });
    card.appendChild(btn);

    const secours = document.createElement('div');
    secours.className = 'sic-secours';
    if (lien) {
        const a = document.createElement('a');
        a.href = lien; a.className = 'sic-lien'; a.textContent = lien;
        secours.appendChild(a);
    }
    if (code) {
        const cspan = document.createElement('span');
        cspan.className = 'sic-code'; cspan.textContent = ` (code : ${code})`;
        secours.appendChild(cspan);
    }
    card.appendChild(secours);
    container.appendChild(card);
}

// ==========================================================================
//   SONDAGES
// ==========================================================================

let _sondageMode = 'multi';

export function creerSondage() {
    const q = document.getElementById('sondage-question').value.trim();
    const err = document.getElementById('sondage-erreur');
    const opts = [...document.querySelectorAll('#sondage-options .sondage-opt')]
        .map(i => i.value.trim()).filter(v => v.length > 0);
    if (window.destinataireID || state.destinataireID) { err.textContent = t('poll.no_mp'); return; }
    if (!q) { err.textContent = 'Mets une question.'; return; }
    if (opts.length < 2) { err.textContent = 'Au moins 2 options non vides.'; return; }
    if (opts.length > 10) { err.textContent = 'Maximum 10 options.'; return; }

    const pollId = crypto.randomUUID();
    const dureeMs = parseInt(document.getElementById('sondage-duree')?.value, 10) || 0;
    const expireAt = dureeMs > 0 ? (Date.now() + dureeMs) : null;

    const multi = (getSondageMode() !== 'single');
    const anonymous = document.getElementById('sondage-anon-toggle')?.checked !== false;
    const desc = { chaosPoll: 1, id: pollId, q: q, opts: opts, multi: multi, anonymous: anonymous };
    if (expireAt) desc.expire_at = expireAt;
    const descripteur = JSON.stringify(desc);

    socket.emit('creer-sondage', { pollId, expireAt, multi, anonymous });

    socket.emit('chat message', {
        auteur: state.monPseudo, // On tape dans le state global
        texte: descripteur,
        destinataire: state.destinataireID || 'public', // On tape dans le state global
        salon_id: state.salonActuelID,
        salonId: state.salonActuelID,
        replyTo: null
    });
    document.getElementById('modal-sondage').style.display = 'none';
}

export function getSondageMode() { return _sondageMode; }

export function setSondageMode(m) {
    _sondageMode = (m === 'single') ? 'single' : 'multi';
    document.querySelectorAll('#sondage-mode button').forEach(b => {
        b.classList.toggle('actif', b.dataset.mode === _sondageMode);
    });
}

export function majLabelAnon() {
    const anon = document.getElementById('sondage-anon-toggle')?.checked !== false;
    const lbl  = document.getElementById('sondage-anon-label');
    if (lbl) lbl.textContent = anon
        ? 'Les votes sont anonymes'
        : 'Les votants sont visibles avec leur avatar';
}

export function ouvrirModaleSondage() {
    // Pas de sondage en MP : même chiffré, le vote/clôture/anonymat transitent
    // forcément par le serveur (poll-vote) → incompatible avec l'invariant E2EE.
    if (window.destinataireID || state.destinataireID) {
        popToast(t('poll.no_mp'), 'error');
        return;
    }
    document.getElementById('sondage-question').value = '';
    document.getElementById('sondage-erreur').textContent = '';
    const cont = document.getElementById('sondage-options');
    cont.innerHTML = '';
    ajouterOptionSondage();
    ajouterOptionSondage();
    setSondageMode('multi');
    const anonToggle = document.getElementById('sondage-anon-toggle');
    if (anonToggle) { anonToggle.checked = true; majLabelAnon(); }
    const dureeSel = document.getElementById('sondage-duree');
    if (dureeSel) dureeSel.value = '0';
    document.getElementById('modal-sondage').style.display = 'flex';
    setTimeout(() => document.getElementById('sondage-question')?.focus(), 80);
}

export function ajouterOptionSondage() {
    const cont = document.getElementById('sondage-options');
    if (cont.children.length >= 10) return;
    const row = document.createElement('div');
    row.className = 'sondage-option-row';
    const inp = document.createElement('input');
    inp.type = 'text'; inp.className = 'cyber-input sondage-opt';
    inp.maxLength = 100; inp.placeholder = 'Option ' + (cont.children.length + 1);
    inp.autocomplete = 'off';
    const del = document.createElement('button');
    del.type = 'button'; del.className = 'sondage-opt-del';
    del.textContent = '✕'; del.title = 'Retirer';
    del.onclick = () => { if (cont.children.length > 2) row.remove(); };
    row.appendChild(inp); row.appendChild(del);
    cont.appendChild(row);
}

window._pollMine = window._pollMine || {};

export function renderPoll(container, descripteurStr) {
    let d;
    try { d = JSON.parse(descripteurStr); } catch (_) { container.textContent = '📊'; return; }
    if (!d.id || !Array.isArray(d.opts)) { container.textContent = '📊'; return; }

    const box = document.createElement('div');
    box.className = 'chaos-poll';
    box.dataset.pollId = d.id;
    if (d.expire_at) box.dataset.expireAt = String(d.expire_at);
    box.dataset.anonymous = (d.anonymous === false) ? 'false' : 'true';

    const qEl = document.createElement('div');
    qEl.className = 'poll-question'; qEl.textContent = d.q || '';
    box.appendChild(qEl);

    const modeEl = document.createElement('div');
    modeEl.className = 'poll-mode';
    box.appendChild(modeEl);
    _appliquerModeSondage(box, d.multi === false ? false : true);

    const anonEl = document.createElement('div');
    anonEl.className = 'poll-anon-badge';
    if (d.anonymous === false) anonEl.textContent = '👁️ Non-anonyme';
    box.appendChild(anonEl);

    d.opts.forEach((label, i) => {
        const row  = document.createElement('button');
        row.type   = 'button'; row.className = 'poll-option';
        row.dataset.opt = String(i);

        const fill = document.createElement('span'); fill.className = 'poll-fill';
        const txt  = document.createElement('span'); txt.className  = 'poll-opt-label'; txt.textContent = label;
        const cnt  = document.createElement('span'); cnt.className  = 'poll-opt-count'; cnt.textContent = '0';

        row.appendChild(fill); row.appendChild(txt); row.appendChild(cnt);
        row.addEventListener('click', (e) => {
            e.stopPropagation();
            if (box.classList.contains('poll-closed')) {
                popToast('🔒 Ce sondage est clôturé.', 'stream');
                return;
            }
            socket.emit('poll-vote', { pollId: d.id, optionIndex: i });
        });
        box.appendChild(row);
    });

    const statut = document.createElement('div'); statut.className = 'poll-statut';
    box.appendChild(statut);
    const foot = document.createElement('div'); foot.className = 'poll-foot'; foot.textContent = '0 votant';
    box.appendChild(foot);

    container.appendChild(box);
    _rafraichirClotureSondage(box);
    socket.emit('poll-get', { pollId: d.id });
}

function _rafraichirClotureSondage(box) {
    const statutEl = box.querySelector('.poll-statut');
    const expireAt = box.dataset.expireAt ? Number(box.dataset.expireAt) : null;

    if (box._sbCloseTimer) { clearTimeout(box._sbCloseTimer); box._sbCloseTimer = null; }
    if (!expireAt) { box.classList.remove('poll-closed'); if (statutEl) statutEl.textContent = ''; return; }

    const reste = expireAt - Date.now();
    if (reste <= 0) {
        box.classList.add('poll-closed');
        if (statutEl) statutEl.textContent = '🔒 Sondage clôturé';
        return;
    }
    box.classList.remove('poll-closed');
    if (statutEl) {
        const min = Math.round(reste / 60000);
        let txt;
        if (min < 1)       txt = "moins d'une minute";
        else if (min < 60) txt = `${min} min`;
        else if (min < 60 * 24) txt = `${Math.round(min / 60)} h`;
        else               txt = `${Math.round(min / 1440)} j`;
        statutEl.textContent = `⏳ Clôture dans ~${txt}`;
    }
    box._sbCloseTimer = setTimeout(() => _rafraichirClotureSondage(box), Math.min(reste, 2147483000));
}

function _appliquerModeSondage(box, multi) {
    const el = box.querySelector('.poll-mode');
    if (!el) return;
    const single = (multi === false);
    el.textContent = single ? '◉ Choix unique' : '☑ Choix multiple';
    box.classList.toggle('poll-single', single);
}

function _appliquerPollUpdate({ pollId, counts, total, expireAt, multi, anonymous, voters } = {}) {
    counts = counts || {};
    total  = total  || 0;
    document.querySelectorAll(`.chaos-poll[data-poll-id="${CSS.escape(pollId)}"]`).forEach(box => {
        if (multi     !== undefined) _appliquerModeSondage(box, multi);
        if (anonymous !== undefined) {
            box.dataset.anonymous = anonymous ? 'true' : 'false';
            const anonEl = box.querySelector('.poll-anon-badge');
            if (anonEl) anonEl.textContent = anonymous === false ? '👁️ Non-anonyme' : '';
        }
        if (expireAt !== undefined) {
            if (expireAt) box.dataset.expireAt = String(expireAt);
            else delete box.dataset.expireAt;
            _rafraichirClotureSondage(box);
        }
        let max = 0;
        Object.values(counts).forEach(n => { if (n > max) max = n; });
        const isAnon = anonymous !== undefined ? anonymous : box.dataset.anonymous !== 'false';
        box.querySelectorAll('.poll-option').forEach(row => {
            const i   = Number(row.dataset.opt);
            const n   = counts[i] || 0;
            const pct = total > 0 ? Math.round((n / total) * 100) : 0;
            const fill = row.querySelector('.poll-fill');
            if (fill) fill.style.width = pct + '%';
            const c = row.querySelector('.poll-opt-count');
            if (c) c.textContent = pct ? `${n} · ${pct}%` : String(n);
            row.classList.toggle('poll-leading', n > 0 && n === max);

            let votersEl = box.querySelector(`.poll-voters[data-opt="${i}"]`);
            const optVoters = !isAnon && voters ? (voters[i] || []) : [];
            if (optVoters.length > 0) {
                if (!votersEl) {
                    votersEl = document.createElement('div');
                    votersEl.className = 'poll-voters';
                    votersEl.dataset.opt = String(i);
                    row.insertAdjacentElement('afterend', votersEl);
                }
                votersEl.innerHTML = '';
                const MAX = 7;
                optVoters.slice(0, MAX).forEach(v => {
                    const chip = document.createElement('span');
                    chip.className = 'poll-voter-chip';
                    const img = document.createElement('img');
                    img.src = v.avatar || ''; img.alt = '';
                    if (!v.avatar) img.style.visibility = 'hidden';
                    chip.appendChild(img);
                    chip.appendChild(document.createTextNode(v.pseudo));
                    votersEl.appendChild(chip);
                });
                if (optVoters.length > MAX) {
                    const ov = document.createElement('span');
                    ov.className = 'poll-voter-overflow';
                    ov.textContent = `+${optVoters.length - MAX}`;
                    votersEl.appendChild(ov);
                }
            } else if (votersEl) {
                votersEl.remove();
            }
        });
        const foot = box.querySelector('.poll-foot');
        if (foot) foot.textContent = total + (total > 1 ? ' votants' : ' votant');
    });
}

function _appliquerPollMine({ pollId, mine } = {}) {
    const set = new Set(mine || []);
    window._pollMine[pollId] = set;
    document.querySelectorAll(`.chaos-poll[data-poll-id="${CSS.escape(pollId)}"]`).forEach(box => {
        box.querySelectorAll('.poll-option').forEach(row => {
            row.classList.toggle('poll-chosen', set.has(Number(row.dataset.opt)));
        });
    });
}

socket.on('poll-update', (data) => { _appliquerPollUpdate(data); scrollIfNearBottom(); });
socket.on('poll-mine',   _appliquerPollMine);